import styled from "@emotion/styled";
import { Flex, Text } from "styles/utility-components";

import Badge from "components/ui/badge/Badge";
import Button from "components/ui/button/Button";
import Card from "components/ui/card/Card";
import Divider from "components/ui/divider/Divider";
import { useSsoProviders } from "lib/hooks/api/auth/sso/useSsoProviders";
import { useAssertCurrentUser } from "lib/hooks/api/users/useCurrentUser";
import UIcon, { CustomIconKey } from "lib/icons/untitled_ui/UIcon";
import { generateProviderId, ProviderName, SSOProtocols } from "./ProviderUtils";

type ProviderPresetBase = {
  name: string; // User-facing name
  icon: CustomIconKey;
  providerName: ProviderName; // Used for building the callback URL
  placeholders?: {
    issuer?: string;
    entryPoint?: string;
    idpEntityId?: string;
  };
  defaultValues?: {
    issuer?: string;
    mappingEmail?: string;
    mappingName?: string;
    mappingFirstName?: string;
    mappingLastName?: string;
  };
  guideUrl?: string;
};

export type ProviderPreset = ProviderPresetBase & {
  key: string;
  protocol: SSOProtocols;
};

const PROVIDER_PRESENTS_OIDC: ProviderPresetBase[] = [
  {
    providerName: "microsoft",
    name: "Microsoft Entra ID",
    icon: "microsoft",
    placeholders: {
      issuer: "https://login.microsoftonline.com/<tenant-id>/v2.0",
    },
  },
  {
    providerName: "google",
    name: "Google Workspace",
    icon: "google",
    defaultValues: {
      issuer: "https://accounts.google.com",
    },
  },
  {
    providerName: "okta",
    name: "Okta",
    icon: "okta",
    placeholders: {
      issuer: "https://<your-domain>.okta.com",
    },
  },
  {
    providerName: "onelogin",
    name: "OneLogin",
    icon: "onelogin",
    placeholders: {
      issuer: "https://<your-domain>.onelogin.com/oidc/2",
    },
  },
  {
    providerName: "keycloak",
    name: "Keycloak",
    icon: "keycloak",
    placeholders: {
      issuer: "https://<your-domain>/auth/realms/<realm-name>",
    },
  },
  {
    providerName: "custom",
    name: "Custom OIDC",
    icon: "openIdConnect",
  },
];

const PROVIDER_PRESENTS_SAML: ProviderPresetBase[] = [
  {
    providerName: "microsoft",
    name: "Microsoft Entra ID",
    icon: "microsoft",
    placeholders: {
      entryPoint: "https://login.microsoftonline.com/<tenant-id>/saml2",
      idpEntityId: "https://sts.windows.net/<tenant-id>/",
    },
    defaultValues: {
      mappingEmail: "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress",
      mappingName: "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name",
      mappingFirstName: "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/givenname",
      mappingLastName: "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/surname",
    },
  },
  {
    providerName: "google",
    name: "Google Workspace",
    icon: "google",
    placeholders: {
      entryPoint: "https://accounts.google.com/o/saml2/idp?idpid=<identifier>",
      idpEntityId: "https://accounts.google.com/o/saml2?idpid=<identifier>",
    },
    defaultValues: {
      mappingEmail: "email",
      mappingName: "displayName",
      mappingFirstName: "firstName",
      mappingLastName: "lastName",
    },
  },
  {
    providerName: "okta",
    name: "Okta",
    icon: "okta",
    placeholders: {
      entryPoint: "https://<your-domain>.okta.com/app/<app-id>/sso/saml",
      idpEntityId: "http://www.okta.com/<app-id>",
    },
    defaultValues: {
      mappingEmail: "user.email",
      mappingName: "user.displayName",
      mappingFirstName: "user.firstName",
      mappingLastName: "user.lastName",
    },
  },
  {
    providerName: "custom",
    name: "Custom SAML",
    icon: "saml",
  },
];

// We support autodiscovery with the following other providers but they currently
// don't have an entry since I couldnt't find a good logo for them & they are not
// common & we haven't tested they work properly so I've just removed them for now
// - PingOne, oidc issuer = https://auth.pingone.com/<environment-id>/as
// - JumpCloud, oidc issuer = https://oauth.id.jumpcloud.com (also https://oauth.id.<region>.jumpcloud.com)
// - CyberArk, oidc issuer = https://<tenant>.id.cyberark.cloud
const PROVIDER_PRESETS: ProviderPreset[] = [
  ...PROVIDER_PRESENTS_OIDC.map((provider) => ({
    ...provider,
    protocol: "oidc" as const,
    key: `${provider.providerName}-oidc`,
  })),
  ...PROVIDER_PRESENTS_SAML.map((provider) => ({
    ...provider,
    protocol: "saml" as const,
    key: `${provider.providerName}-saml`,
  })),
];

export function getPresetByKey(key: string): ProviderPreset | undefined {
  return PROVIDER_PRESETS.find((p) => p.key === key);
}

function PresetGrid({
  protocol,
  organizationId,
  existingProviderIds,
}: {
  protocol: SSOProtocols;
  organizationId: string;
  existingProviderIds: Set<string>;
}) {
  const presets = PROVIDER_PRESETS.filter((p) => p.protocol === protocol);
  return (
    <Grid>
      {presets.map((preset) => {
        const providerId = generateProviderId(organizationId, preset.protocol, preset.providerName);
        const alreadyExists = existingProviderIds.has(providerId);
        return (
          <Card
            key={preset.key}
            to="/account-management/sso/add/$providerKey"
            params={{ providerKey: preset.key }}
            borderRadius={8}
            padding={20}
            disabled={alreadyExists}
          >
            <Flex gap={8} alignItems="center" justifyContent="space-between">
              <Flex gap={8} alignItems="center" minWidth={0}>
                {preset.icon && <UIcon icon={preset.icon} size={24} />}
                <Text h3 ellipsis>
                  {preset.name}
                </Text>
              </Flex>
              <Badge fitContent badgeType="secondary">
                {alreadyExists ? "Configured" : preset.protocol.toUpperCase()}
              </Badge>
            </Flex>
          </Card>
        );
      })}
    </Grid>
  );
}

export default function ProviderSelector() {
  const currentUser = useAssertCurrentUser();
  const organizationId = currentUser.company.guid;
  const { data: providers } = useSsoProviders();

  const existingProviderIds = new Set(providers?.map((p) => p.providerId));

  return (
    <Flex column gap={24}>
      <Flex column gap={8}>
        <Text h1>Select provider</Text>
        <Text description>Choose the type of SSO provider you want to configure.</Text>
      </Flex>

      <Flex column gap={8}>
        <Text h2>OpenID Connect (recommended)</Text>
        <Text description>
          OIDC is simpler to configure and we recommend picking it over SAML if your identity
          provider supports it. This is an extension of the OAuth 2.0 protocol.
        </Text>
      </Flex>
      <PresetGrid
        protocol="oidc"
        organizationId={organizationId}
        existingProviderIds={existingProviderIds}
      />

      <Divider />

      <Flex column gap={8}>
        <Text h2>SAML 2.0</Text>
        <Text description>
          Use SAML only if your identity provider does not support OIDC or your organisation
          requires it.
        </Text>
      </Flex>
      <PresetGrid
        protocol="saml"
        organizationId={organizationId}
        existingProviderIds={existingProviderIds}
      />

      <Button
        as="a"
        to="/account-management/sso"
        buttonType="secondary"
        activeOptions={{ exact: true }}
        width="fit-content"
      >
        Back
      </Button>
    </Flex>
  );
}

const Grid = styled.div({
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
  gap: 16,
});
